import * as THREE from 'https://unpkg.com/three@0.160.0/build/three.module.js';

const CLOTH_SEGMENTS_X = 48;
const CLOTH_SEGMENTS_Y = 64;
const MAX_PIXEL_RATIO = 1.25;
const MAX_TEXTURE_ANISOTROPY = 4;

const readNumber = (element, name, fallback) => {
  const value = Number.parseFloat(element.dataset[name]);
  return Number.isFinite(value) ? value : fallback;
};

const readScaledNumber = (element, name, fallback, scale) => readNumber(element, name, fallback / scale) * scale;

const readBoolean = (element, name, fallback) => {
  const value = element.dataset[name];
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
};

const buildConfig = (stage) => ({
  interaction: {
    follow: readNumber(stage, 'interactionFollow', 0.08),
    returnSpeed: readScaledNumber(stage, 'interactionReturnSpeed', 0.035, 0.1),
    hoverPull: readNumber(stage, 'interactionHoverPull', 1.5),
    pullRadius: readNumber(stage, 'interactionPullRadius', 0.5),
    cursorXRange: readNumber(stage, 'interactionCursorXRange', 2.25),
    cursorYRange: readNumber(stage, 'interactionCursorYRange', 1.65),
    xPull: readNumber(stage, 'interactionXPull', 0.4),
    yPull: readNumber(stage, 'interactionYPull', 0.28),
    depthPull: readNumber(stage, 'interactionDepthPull', 0.92),
    waveStrength: readNumber(stage, 'interactionWaveStrength', 0.18),
    waveFrequency: readNumber(stage, 'interactionWaveFrequency', 1.5),
    waveSpeed: readNumber(stage, 'interactionWaveSpeed', 2.6),
    rotationY: readNumber(stage, 'interactionRotationY', 0.5),
    rotationX: readNumber(stage, 'interactionRotationX', 0.15),
    rotationZ: readNumber(stage, 'interactionRotationZ', 0.05),
  },
  intro: {
    duration: readNumber(stage, 'introDuration', 1.35),
    startY: readNumber(stage, 'introStartY', 6.8),
    targetY: readNumber(stage, 'introTargetY', 0.85),
    bounce: readNumber(stage, 'introBounce', 0.18),
  },
  slider: {
    min: readNumber(stage, 'sliderMin', 0.2),
    max: readNumber(stage, 'sliderMax', 2),
    step: readNumber(stage, 'sliderStep', 0.01),
    value: readNumber(stage, 'sliderValue', 1),
    repeatBase: readNumber(stage, 'repeatBase', 3.2),
  },
  geometry: {
    width: readNumber(stage, 'clothWidth', 5),
    height: readNumber(stage, 'clothHeight', 6.5),
  },
  material: {
    roughness: readNumber(stage, 'materialRoughness', 0.6),
    metalness: readNumber(stage, 'materialMetalness', 0),
    sheen: readNumber(stage, 'materialSheen', 0.1),
    normalStrength: readNumber(stage, 'materialNormalStrength', 1.2),
    normalRepeat: readNumber(stage, 'materialNormalRepeat', 2.5),
  },
  camera: {
    desktopZ: readNumber(stage, 'cameraDesktopZ', 8.35),
    mobileZ: readNumber(stage, 'cameraMobileZ', 9.55),
  },
  uploadEnabled: readBoolean(stage, 'uploadEnabled', true),
});

const prepareTexture = (texture, anisotropy) => {
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = anisotropy;
  return texture;
};

const prepareNormalTexture = (texture, anisotropy) => {
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.NoColorSpace;
  texture.anisotropy = anisotropy;
  return texture;
};

const initFabricHero = (stage) => {
  if (stage.dataset.mmooiFabricHeroInitialized === 'true') return;
  stage.dataset.mmooiFabricHeroInitialized = 'true';

  const config = buildConfig(stage);
  const app = stage.querySelector('[data-fabric-canvas]');
  const loading = stage.querySelector('[data-fabric-loading]');
  const scaleInput = stage.querySelector('[data-fabric-scale]');
  const uploadInput = stage.querySelector('[data-fabric-upload]');
  const uploadThumb = stage.querySelector('[data-upload-thumb]');
  const patternButtons = Array.from(stage.querySelectorAll('[data-pattern-index]'));
  const patternAssets = [stage.dataset.patternOne, stage.dataset.patternTwo].filter(Boolean);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!app || !scaleInput || patternAssets.length === 0) return;

  scaleInput.min = String(config.slider.min);
  scaleInput.max = String(config.slider.max);
  scaleInput.step = String(config.slider.step);
  scaleInput.value = String(config.slider.value);
  scaleInput.setAttribute('aria-valuemin', String(config.slider.min));
  scaleInput.setAttribute('aria-valuemax', String(config.slider.max));

  const renderer = new THREE.WebGLRenderer({
    antialias: false,
    alpha: false,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO));
  renderer.setSize(stage.clientWidth, stage.clientHeight);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;
  app.appendChild(renderer.domElement);
  const textureAnisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), MAX_TEXTURE_ANISOTROPY);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xffffff);

  // The cloth's animated folds can move behind the base plane, especially on mobile.
  const camera = new THREE.PerspectiveCamera(45, stage.clientWidth / stage.clientHeight, 0.1, 30);
  camera.position.set(0, 0, config.camera.desktopZ);

  scene.add(new THREE.HemisphereLight(0xffffff, 0xe8e8e8, 0.75));
  const keyLight = new THREE.DirectionalLight(0xffffff, 2.1);
  keyLight.position.set(3.8, 4.8, 5.5);
  scene.add(keyLight);
  const rimLight = new THREE.DirectionalLight(0xffffff, 0.35);
  rimLight.position.set(-4, 1.8, 3);
  scene.add(rimLight);

  const textureLoader = new THREE.TextureLoader();
  const textures = patternAssets.map((url, index) => {
    const texture = textureLoader.load(url, (loadedTexture) => {
      loadedTexture.userData.aspect = loadedTexture.image.naturalHeight / loadedTexture.image.naturalWidth;
      updateScale();
    });
    patternButtons[index]?.style.setProperty('background-image', `url(${url})`);
    return prepareTexture(texture, textureAnisotropy);
  });

  let material;
  const normalTexture = stage.dataset.normalMap
    ? prepareNormalTexture(
        textureLoader.load(stage.dataset.normalMap, undefined, undefined, () => {
          if (!material) return;
          material.normalMap = null;
          material.needsUpdate = true;
        }),
        textureAnisotropy
      )
    : null;
  const normalRepeatY = config.material.normalRepeat * (2 * config.geometry.height / config.geometry.width);
  normalTexture?.repeat.set(config.material.normalRepeat, normalRepeatY);

  // The curtain is deformed on the GPU. This keeps the animation smooth without
  // rewriting thousands of vertices and recalculating all normals on every frame.
  const geometry = new THREE.PlaneGeometry(
    config.geometry.width,
    config.geometry.height,
    CLOTH_SEGMENTS_X,
    CLOTH_SEGMENTS_Y
  );
  let fabricShader = null;
  material = new THREE.MeshPhysicalMaterial({
    map: textures[0],
    normalMap: normalTexture,
    // The supplied map is DirectX, whose green channel is inverted compared with WebGL.
    normalScale: new THREE.Vector2(config.material.normalStrength, -config.material.normalStrength),
    color: 0xffffff,
    roughness: config.material.roughness,
    metalness: config.material.metalness,
    sheen: config.material.sheen,
    sheenColor: new THREE.Color(0xffffff),
    sheenRoughness: 1,
    clearcoat: 0,
    clearcoatRoughness: 1,
    side: THREE.DoubleSide,
  });
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uFabricTime = { value: 0 };
    shader.uniforms.uFabricSize = {
      value: new THREE.Vector2(config.geometry.width, config.geometry.height),
    };
    shader.uniforms.uFabricMotionScale = { value: reducedMotion ? 0 : 1 };

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float uFabricTime;
uniform vec2 uFabricSize;
uniform float uFabricMotionScale;

vec3 mmooiCurtainOffset(vec3 point) {
  float u = clamp(point.x / uFabricSize.x + 0.5, 0.0, 1.0);
  float v = clamp(point.y / uFabricSize.y + 0.5, 0.0, 1.0);
  float fromTop = 1.0 - v;
  float freeFromTop = smoothstep(0.015, 0.22, fromTop);
  float lowerFreedom = smoothstep(0.48, 1.0, fromTop);
  float edgeFreedom = pow(abs(u - 0.5) * 2.0, 2.35);
  float animatedTime = uFabricTime * uFabricMotionScale;

  // Narrow vertical folds remain visible at rest. Broader, asymmetric waves
  // create the silhouette of a curtain moving through a light breeze.
  float pleats = sin(u * PI * 8.0 + fromTop * 0.48 + sin(animatedTime * 0.24) * 0.4);
  float broadFold = sin(u * PI * 3.25 - fromTop * 2.55 + animatedTime * 0.42);
  float diagonalWind = sin(u * 4.7 + fromTop * 3.2 + animatedTime * 0.58);
  float slowWind = sin(fromTop * PI * 1.35 - animatedTime * 0.36 + u * 1.1);
  float flutter = sin(u * PI * 10.0 + fromTop * 5.4 + animatedTime * 1.08);
  float sideSway = sin(fromTop * PI * 0.9 + animatedTime * 0.38);

  vec3 offset = vec3(0.0);
  offset.z += pleats * mix(0.46, 0.28, fromTop);
  offset.z += broadFold * 0.44 * freeFromTop;
  offset.z += diagonalWind * slowWind * 0.36 * freeFromTop;
  offset.z += flutter * 0.18 * (lowerFreedom + edgeFreedom * 0.65) * freeFromTop;
  offset.x += (slowWind * 0.23 + diagonalWind * 0.11) * freeFromTop;
  offset.x += sideSway * 0.23 * freeFromTop;
  offset.x += flutter * 0.19 * edgeFreedom * freeFromTop;
  offset.y += diagonalWind * 0.1 * freeFromTop;
  offset.y += flutter * 0.16 * lowerFreedom * freeFromTop;
  return offset;
}`
      )
      .replace(
        '#include <beginnormal_vertex>',
        `vec3 mmooiBasePosition = position;
float mmooiNormalStep = min(
  uFabricSize.x / ${CLOTH_SEGMENTS_X.toFixed(1)},
  uFabricSize.y / ${CLOTH_SEGMENTS_Y.toFixed(1)}
);
vec3 mmooiCurrentPosition = mmooiBasePosition + mmooiCurtainOffset(mmooiBasePosition);
vec3 mmooiRightBase = mmooiBasePosition + vec3(mmooiNormalStep, 0.0, 0.0);
vec3 mmooiUpperBase = mmooiBasePosition + vec3(0.0, mmooiNormalStep, 0.0);
vec3 mmooiTangentX = mmooiRightBase + mmooiCurtainOffset(mmooiRightBase) - mmooiCurrentPosition;
vec3 mmooiTangentY = mmooiUpperBase + mmooiCurtainOffset(mmooiUpperBase) - mmooiCurrentPosition;
vec3 objectNormal = normalize(cross(mmooiTangentX, mmooiTangentY));
#ifdef USE_TANGENT
  vec3 objectTangent = vec3(tangent.xyz);
#endif`
      )
      .replace('#include <begin_vertex>', 'vec3 transformed = position + mmooiCurtainOffset(position);');

    fabricShader = shader;
  };
  material.customProgramCacheKey = () => 'mmooi-wind-curtain-v4';
  const cloth = new THREE.Mesh(geometry, material);
  const baseRotationY = THREE.MathUtils.degToRad(-38);
  cloth.position.y = reducedMotion ? config.intro.targetY : config.intro.startY;
  scene.add(cloth);

  const mouse = { x: 0, y: 0, tx: 0, ty: 0, inside: false };
  stage.addEventListener('pointerenter', () => {
    mouse.inside = true;
  });
  stage.addEventListener('pointerleave', () => {
    mouse.inside = false;
  });
  stage.addEventListener('pointermove', (event) => {
    const rect = stage.getBoundingClientRect();
    mouse.inside = true;
    mouse.tx = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
    mouse.ty = -((event.clientY - rect.top) / rect.height - 0.5) * 2;
  });

  const updateInteraction = () => {
    if (!mouse.inside) {
      mouse.tx += (0 - mouse.tx) * config.interaction.returnSpeed;
      mouse.ty += (0 - mouse.ty) * config.interaction.returnSpeed;
    }

    mouse.x += (mouse.tx - mouse.x) * config.interaction.follow;
    mouse.y += (mouse.ty - mouse.y) * config.interaction.follow;
  };

  function updateScale() {
    const value = Number.parseFloat(scaleInput.value);
    const repeat = config.slider.repeatBase / value;
    const progress = ((value - config.slider.min) / (config.slider.max - config.slider.min)) * 100;
    const aspect = material.map?.userData.aspect || 1;
    const readableValue = Number.isFinite(value) ? value.toFixed(2).replace(/\.?0+$/, '') : '';
    material.map.repeat.set(repeat * aspect, repeat);
    scaleInput.style.background = `linear-gradient(90deg, #f42aae 0%, #f42aae ${progress}%, #cfcfcf ${progress}%, #cfcfcf 100%)`;
    scaleInput.setAttribute('aria-valuenow', readableValue);
    scaleInput.setAttribute('aria-valuetext', `${scaleInput.getAttribute('aria-label') || 'Mustergröße'} ${readableValue}`);
  }

  scaleInput.addEventListener('input', updateScale);
  updateScale();

  patternButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const patternIndex = Number.parseInt(button.dataset.patternIndex, 10);
      if (!textures[patternIndex]) return;
      stage.querySelectorAll('.thumb').forEach((thumb) => thumb.classList.remove('active'));
      patternButtons.forEach((patternButton) => patternButton.setAttribute('aria-pressed', 'false'));
      button.classList.add('active');
      button.setAttribute('aria-pressed', 'true');
      material.map = textures[patternIndex];
      updateScale();
      material.needsUpdate = true;
    });
  });

  let uploadObjectUrl = '';
  if (config.uploadEnabled && uploadInput && uploadThumb) {
    uploadInput.addEventListener('change', () => {
      const file = uploadInput.files?.[0];
      if (!file) return;
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        uploadInput.value = '';
        return;
      }
      if (uploadObjectUrl) URL.revokeObjectURL(uploadObjectUrl);
      uploadObjectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        const texture = prepareTexture(new THREE.Texture(image), textureAnisotropy);
        texture.userData.aspect = image.naturalHeight / image.naturalWidth;
        texture.needsUpdate = true;
        stage.querySelectorAll('.thumb').forEach((thumb) => thumb.classList.remove('active'));
        patternButtons.forEach((patternButton) => patternButton.setAttribute('aria-pressed', 'false'));
        uploadThumb.classList.add('active', 'has-image');
        uploadThumb.style.setProperty('background-image', `url(${uploadObjectUrl})`);
        material.map = texture;
        updateScale();
        material.needsUpdate = true;
      };
      image.src = uploadObjectUrl;
    });
  } else {
    uploadThumb?.remove();
  }

  const resize = () => {
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    renderer.setSize(width, height);
    camera.aspect = width / height;
    camera.position.z = camera.aspect < 0.9 ? config.camera.mobileZ : config.camera.desktopZ;
    camera.updateProjectionMatrix();
  };
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(stage);
  resize();

  const startedAt = performance.now();
  let isInViewport = true;
  let animationFrame = 0;
  let disposed = false;
  const easeOutBack = (x) => {
    const c1 = 1.70158 * config.intro.bounce;
    const c3 = c1 + 1;
    return 1 + c3 * ((x - 1) ** 3) + c1 * ((x - 1) ** 2);
  };

  const disposeHero = () => {
    if (disposed) return;
    disposed = true;
    if (animationFrame) cancelAnimationFrame(animationFrame);
    resizeObserver.disconnect();
    visibilityObserver.disconnect();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    geometry.dispose();
    new Set([...textures, normalTexture, material.map].filter(Boolean)).forEach((texture) => texture.dispose());
    material.dispose();
    renderer.dispose();
    if (uploadObjectUrl) URL.revokeObjectURL(uploadObjectUrl);
  };

  const animate = () => {
    animationFrame = 0;
    if (!stage.isConnected) {
      disposeHero();
      return;
    }
    if (!isInViewport || document.hidden) return;

    const time = (performance.now() - startedAt) / 1000;
    const introProgress = reducedMotion ? 1 : Math.min(time / config.intro.duration, 1);
    const easedIntro = easeOutBack(introProgress);
    updateInteraction();
    if (fabricShader) {
      fabricShader.uniforms.uFabricTime.value = time;
    }
    cloth.position.y = config.intro.startY + (config.intro.targetY - config.intro.startY) * easedIntro;
    cloth.rotation.y = baseRotationY + mouse.x * config.interaction.rotationY;
    cloth.rotation.x = -mouse.y * config.interaction.rotationX;
    cloth.rotation.z = (reducedMotion ? 0 : Math.sin(time * 0.22) * 0.022) + mouse.x * config.interaction.rotationZ;
    renderer.render(scene, camera);
    animationFrame = requestAnimationFrame(animate);
  };

  const scheduleAnimation = () => {
    if (disposed || animationFrame || !isInViewport || document.hidden) return;
    animationFrame = requestAnimationFrame(animate);
  };

  const visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      isInViewport = entry.isIntersecting;
      scheduleAnimation();
    },
    { rootMargin: '200px' }
  );
  visibilityObserver.observe(stage);

  function handleVisibilityChange() {
    scheduleAnimation();
  }
  document.addEventListener('visibilitychange', handleVisibilityChange);
  stage.mmooiFabricHeroCleanup = disposeHero;

  loading?.classList.add('hidden');
  scheduleAnimation();
};

const initAllFabricHeroes = () => {
  document.querySelectorAll('[data-mmooi-fabric-hero]').forEach(initFabricHero);
};

initAllFabricHeroes();
document.addEventListener('shopify:section:load', initAllFabricHeroes);
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-mmooi-fabric-hero]').forEach((stage) => {
    stage.mmooiFabricHeroCleanup?.();
  });
});
